import { debounce } from 'lodash-es';
import { DateTime } from 'luxon';
import { create } from 'zustand';
import { Budget, CategoryWithBuckets, CollapseState, TableView } from '@liquidbudget/common';
import { apiClient } from '@/api-client';

export type BudgetView = { type: 'all' } | { type: 'archived' } | { type: 'custom'; viewId: string };
export type ColumnView = 'Available' | 'Outflow' | 'Assigned' | 'Leftover';

type BudgetPageStore = {
  year: number;
  setYear: (year: number) => void;
  month: number;
  setMonth: (month: number) => void;
  isAsideOpen: boolean;
  setIsAsideOpen: (isOpen: boolean) => void;
  selectedView: BudgetView;
  setSelectedView: (view: BudgetView) => void;
  columnView: ColumnView;
  setColumnView: (view: ColumnView) => void;
  nonZero: boolean;
  setNonZero: (nonZero: boolean) => void;
  checkedBuckets: Set<string>;
  checkedCategories: Set<string>;
  toggleCheckedBucket: (id: string) => void;
  toggleCheckedCategory: (category: CategoryWithBuckets) => void;
  toggleAll: (categories: CategoryWithBuckets[]) => void;
  uncheckAll: () => void;
  budget: Budget | null;
  setBudget: (budget: Budget) => void;
  setCollapseState: (collapseState: CollapseState) => void;
  setCollapsedCategoryIds: (ids: string[]) => void;
  setTableView: (tableView: TableView) => void;
  initializeDate: (timezone?: string) => void;
};

// Debounced API sync function (5 second delay)
const debouncedSyncBudget = debounce(async (budget: Budget) => {
  // TODO: I don't like this
  await apiClient.put(`/budget`, budget);
}, 5000);

// Create a function to get date from URL or current date with timezone
const getDateFromUrlOrCurrent = (timezone?: string): { year: number; month: number } => {
  const params = new URLSearchParams(window.location.search);
  const dateParam = params.get('date');

  // If URL has date parameter, parse it
  if (dateParam) {
    const [yearStr, monthStr] = dateParam.split('-');
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10);

    if (!isNaN(year) && !isNaN(month) && month >= 1 && month <= 12) {
      return { year, month };
    }
  }

  // Otherwise use current date with timezone if available
  const now = timezone ? DateTime.now().setZone(timezone) : DateTime.now();

  return {
    year: now.year,
    month: now.month,
  };
};

export const useBudgetPageStore = create<BudgetPageStore>()((set, get) => ({
  // Initialize with defaults - will be updated when initializeDate is called
  year: new Date().getFullYear(),
  month: new Date().getMonth() + 1,

  setYear: (year) => set({ year }),
  setMonth: (month) => set({ month }),

  // Add initialization function that can be called after getting user info
  initializeDate: (timezone?: string) => {
    const { year, month } = getDateFromUrlOrCurrent(timezone);
    set({ year, month });
  },

  isAsideOpen: false,
  setIsAsideOpen: (isAsideOpen: boolean) => set({ isAsideOpen }),

  selectedView: { type: 'all' },
  setSelectedView: (selectedView) => {
    set({
      selectedView,
      checkedBuckets: new Set<string>(),
      checkedCategories: new Set<string>(),
      nonZero: false,
    });
  },

  columnView: 'Available',
  setColumnView: (columnView) => set({ columnView }),

  nonZero: false,
  setNonZero: (nonZero) => set({ nonZero }),

  checkedCategories: new Set<string>(),
  checkedBuckets: new Set<string>(),

  toggleCheckedBucket: (bucketId: string) =>
    set((state) => {
      const newCheckedBuckets = new Set(state.checkedBuckets);
      if (newCheckedBuckets.has(bucketId)) {
        newCheckedBuckets.delete(bucketId);
      } else {
        newCheckedBuckets.add(bucketId);
      }
      return { checkedBuckets: newCheckedBuckets };
    }),

  toggleCheckedCategory: (category: CategoryWithBuckets) =>
    set((state) => {
      const newCheckedBuckets = new Set(state.checkedBuckets);
      const newCheckedCategories = new Set(state.checkedCategories);

      if (category.buckets.length === 0) {
        // For empty categories, toggle the category itself
        if (newCheckedCategories.has(category.id)) {
          newCheckedCategories.delete(category.id);
        } else {
          newCheckedCategories.add(category.id);
        }
      } else {
        // For categories with buckets, check if all buckets are selected
        const areAllBucketsChecked = category.buckets.every((bucket) => newCheckedBuckets.has(bucket.id));

        if (areAllBucketsChecked) {
          // Uncheck all buckets
          category.buckets.forEach((bucket) => newCheckedBuckets.delete(bucket.id));
        } else {
          // Check all buckets
          category.buckets.forEach((bucket) => newCheckedBuckets.add(bucket.id));
        }
      }

      return {
        checkedBuckets: newCheckedBuckets,
        checkedCategories: newCheckedCategories,
      };
    }),

  uncheckAll: () =>
    set({
      checkedBuckets: new Set<string>(),
      checkedCategories: new Set<string>(),
    }),

  toggleAll: (categories: CategoryWithBuckets[]) =>
    set((state) => {
      const newCheckedBuckets = new Set<string>();
      const newCheckedCategories = new Set<string>();

      // Check if everything is currently selected
      let isEverythingChecked = true;

      // Check categories and buckets
      for (const category of categories) {
        if (category.buckets.length === 0) {
          // For empty categories, check if category is selected
          if (!state.checkedCategories.has(category.id)) {
            isEverythingChecked = false;
            break;
          }
        } else {
          // For categories with buckets, check if all buckets are selected
          for (const bucket of category.buckets) {
            if (!state.checkedBuckets.has(bucket.id)) {
              isEverythingChecked = false;
              break;
            }
          }
        }
        if (!isEverythingChecked) break;
      }

      if (!isEverythingChecked) {
        // If not everything is checked, check everything
        categories.forEach((category) => {
          if (category.buckets.length === 0) {
            // For empty categories, add the category
            newCheckedCategories.add(category.id);
          } else {
            // For categories with buckets, add all buckets
            category.buckets.forEach((bucket) => newCheckedBuckets.add(bucket.id));
          }
        });
      }
      // If everything was checked, both Sets remain empty,
      // effectively unchecking everything

      return {
        checkedBuckets: newCheckedBuckets,
        checkedCategories: newCheckedCategories,
      };
    }),

  // Initialize with null budget, later set with actual budget
  budget: null,

  setBudget: (budget) => set({ budget }),

  // Immediate local state update for collapseState, debounced backend sync
  setCollapseState: (collapseState) => {
    set((state) => {
      if (!state.budget) {
        // No change if budget is null, but return an empty object to satisfy Zustand
        return {};
      }

      const updatedBudget = {
        ...state.budget,
        settings: {
          ...state.budget.settings,
          collapseState,
        },
      };

      return { budget: updatedBudget };
    });

    // Call debounced API sync with the updated budget
    debouncedSyncBudget(get().budget!);
  },

  setCollapsedCategoryIds: (ids: string[]) => {
    set((state) => {
      if (!state.budget) {
        // No change if budget is null, but return an empty object to satisfy Zustand
        return {};
      }

      const updatedBudget = {
        ...state.budget,
        settings: {
          ...state.budget.settings,
          collapsedCategoryIds: ids,
        },
      };

      return { budget: updatedBudget };
    });

    // Call debounced API sync with the updated budget
    debouncedSyncBudget(get().budget!);
  },

  setTableView: (tableView) => {
    set((state) => {
      if (!state.budget) {
        return {};
      }

      const updatedBudget = {
        ...state.budget,
        settings: {
          ...state.budget.settings,
          tableView,
        },
      };

      return { budget: updatedBudget };
    });

    debouncedSyncBudget(get().budget!);
  },
}));

// Add this new function to use the initialization with timezone
export const useInitializeDate = () => useBudgetPageStore((state) => state.initializeDate);

// the budget year
export const useYear = () => useBudgetPageStore((state) => state.year);
export const useSetYear = () => useBudgetPageStore((state) => state.setYear);

// the budget month
export const useMonth = () => useBudgetPageStore((state) => state.month);
export const useSetMonth = () => useBudgetPageStore((state) => state.setMonth);

// the aside
export const useIsAsideOpen = () => useBudgetPageStore((state) => state.isAsideOpen);
export const useSetIsAsideOpen = () => useBudgetPageStore((state) => state.setIsAsideOpen);

// the selected view
export const useSelectedView = () => useBudgetPageStore((state) => state.selectedView);
export const useSetSelectedView = () => useBudgetPageStore((state) => state.setSelectedView);

// the column view
export const useColumnView = () => useBudgetPageStore((state) => state.columnView);
export const useSetColumnView = () => useBudgetPageStore((state) => state.setColumnView);

// the non zero filter
export const useNonZero = () => useBudgetPageStore((state) => state.nonZero);
export const useSetNonZero = () => useBudgetPageStore((state) => state.setNonZero);

// the checked buckets
export const useCheckedBuckets = () => useBudgetPageStore((state) => state.checkedBuckets);
export const useCheckedCategories = () => useBudgetPageStore((state) => state.checkedCategories);
export const useToggleCheckedBucket = () => useBudgetPageStore((state) => state.toggleCheckedBucket);
export const useToggleCheckedCategory = () => useBudgetPageStore((state) => state.toggleCheckedCategory);
export const useToggleAll = () => useBudgetPageStore((state) => state.toggleAll);
export const useUncheckAll = () => useBudgetPageStore((state) => state.uncheckAll);

// the budget object
export const useBudget = () => useBudgetPageStore((state) => state.budget);
export const useSetBudget = () => useBudgetPageStore((state) => state.setBudget);

// collapseState getter and setter
export const useCollapseState = () => useBudgetPageStore((state) => state.budget?.settings.collapseState) || 'EXPANDED';
export const useSetCollapseState = () => useBudgetPageStore((state) => state.setCollapseState);

// collapsedCategoryIds getter and setter
export const useCollapsedCategoryIds = () =>
  useBudgetPageStore((state) => state.budget?.settings.collapsedCategoryIds) || [];

export const useSetCollapsedCategoryIds = () => useBudgetPageStore((state) => state.setCollapsedCategoryIds);

// table view
export const useTableView = () => useBudgetPageStore((state) => state.budget?.settings.tableView || TableView.ALL);
export const useSetTableView = () => useBudgetPageStore((state) => state.setTableView);

/**
 * Utility function to return the year and month in a YYYY-MM format.
 */
export const useYearMonth = () => {
  const year = useYear();
  const month = useMonth();
  return `${year}-${month.toString().padStart(2, '0')}`;
};
